import {
  isNodeFinancialStatement,
  isNodeMimeTypeDeepVFile,
} from '@deepcloud/deep-ui-utils'
import type { Node, PathSegment, SearchItem } from '@deepcloud/api-deep-box'

export function getDeepVNodeUrl(nodeId: string) {
  const baseUrl = import.meta.env.VITE_DEEPV_BASE_URL
  if (!baseUrl || !nodeId) return undefined
  return `${baseUrl}/doc/box/${nodeId}`
}

function getFinancialStatementNodeUrl(nodeId: string) {
  const baseUrl = import.meta.env.VITE_FINANCIAL_STATEMENT_FRONTEND_URL
  if (!baseUrl || !nodeId) return undefined
  return `${baseUrl}?nodeId=${nodeId}`
}

function canFinancialStatement(node: Node) {
  if (!node || node.type === undefined) return

  const { type, name } = node

  const baseUrl = import.meta.env.VITE_FINANCIAL_STATEMENT_FRONTEND_URL
  return !!baseUrl && isNodeFinancialStatement({ type, name })
}

export function isExternalNodeLink(node: Node) {
  return isNodeMimeTypeDeepVFile(node.mimeType) || canFinancialStatement(node)
}

export function getExternalNodeLink(node: Node) {
  if (!node || node.type === undefined) return undefined

  const { type, name } = node
  if (isNodeMimeTypeDeepVFile(node.mimeType)) {
    return getDeepVNodeUrl(node.nodeId) ?? undefined
  } else if (isNodeFinancialStatement({ type, name })) {
    return getFinancialStatementNodeUrl(node.nodeId)
  }

  return undefined
}

export function checkNodesPolicy(
  nodes: Node[] | PathSegment[] | SearchItem[],
  policyName: string,
) {
  if (!nodes || !policyName) return false
  for (let i = 0; i < nodes.length; i++) {
    if (nodes[i].policy[policyName] === false) {
      return false
    }
  }
  return true
}

export function checkNodesType(nodes: Node[] | PathSegment[], type: string) {
  if (!nodes || !type) return false
  for (let i = 0; i < nodes.length; i++) {
    if (nodes[i].type !== type) {
      return false
    }
  }
  return true
}

/**
 * Checks if the total size of nodes is within the maxSize limit.
 * @param nodes - Array of Node objects with a numeric `size` property.
 * @param maxSize - Maximum allowed total size in bytes.
 * @returns True if total size is less than or equal to maxSize, false otherwise.
 */
export function checkNodesSize(
  nodes: Node[] = [],
  maxSize: number = 0,
): boolean {
  if (!Array.isArray(nodes) || maxSize <= 0) {
    return false
  }

  const totalSize = nodes.reduce((acc, node) => {
    const nodeSize = node.size ?? 0
    return acc + nodeSize
  }, 0)

  return totalSize <= maxSize
}
